import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { useLocalizeMessage } from 'libs.nucleus';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
const BASE_URL = `//unpkg.com/pdfjs-dist@${pdfjs.version}`;
pdfjs.GlobalWorkerOptions.workerSrc = `${BASE_URL}/build/pdf.worker.min.mjs`;
const pdfOptions = {
    cMapUrl: `https:${BASE_URL}/cmaps/`,
    standardFontDataUrl: `https:${BASE_URL}/standard_fonts/`,
};
const PREVIEW_PAGE_SCALE = 1.25;
function translatedPreviewKind(kind, translate) {
    switch (kind) {
        case 'NUMERIC':
            return translate('NUMERIC');
        case 'EMAIL':
            return translate('EMAIL');
        case 'TEXT':
            return translate('TEXT');
        case 'DATE':
            return translate('DATE');
        case 'CHECKBOX':
            return translate('CHECKBOX');
        case 'SINGLE SELECT':
            return translate('SINGLE SELECT');
        case 'SIGNATURE':
            return translate('SIGNATURE');
        case 'INITIALS':
            return translate('INITIALS');
        default:
            return kind;
    }
}
function previewCaption(overlay, translate) {
    const label = translatedPreviewKind(overlay.kind, translate);
    return overlay.role ? `${label} (${overlay.role})` : label;
}
const overlayClassName = (overlayVariant, highlighted, selectable) => {
    const interaction = selectable ? 'cursor-pointer pointer-events-auto' : 'pointer-events-none';
    if (overlayVariant === 'configuration') {
        return `absolute z-10 box-border flex items-center justify-center rounded border-2 text-center ${interaction} ${highlighted ? 'border-[#4F46E5] bg-[#5F46CF]' : 'border-[#4F46E5] bg-[#F3F2FF]'}`;
    }
    return `absolute z-10 box-border flex items-center justify-center border-2 text-center ${interaction} ${highlighted ? 'border-indigo-700 bg-indigo-600/40' : 'border-blue-600 bg-blue-600/25'}`;
};
/**
 * Read-only PDF + percentage overlays matching the compiled consent `form.json` (same coordinates as the framework app).
 */
export const ConsentCompiledTemplatePreview = ({ pdfDataUrl, overlays = [], signatureFields, focusPage, highlightedElementKey, onSelectElement, className = 'min-h-0 flex-1 overflow-auto bg-neutral-100 p-4', overlayVariant = 'editor', }) => {
    const translate = useLocalizeMessage();
    const [numPages, setNumPages] = useState(0);
    const pageRefs = useRef(new Map());
    const fieldsByPage = useMemo(() => {
        const map = new Map();
        for (const field of signatureFields ?? []) {
            const page = field.page ?? 1;
            const list = map.get(page) ?? [];
            list.push(field);
            map.set(page, list);
        }
        return map;
    }, [signatureFields]);
    const overlaysByPage = useMemo(() => {
        const map = new Map();
        for (const overlay of overlays ?? []) {
            const page = overlay.page || 1;
            const list = map.get(page) ?? [];
            list.push(overlay);
            map.set(page, list);
        }
        return map;
    }, [overlays]);
    useEffect(() => {
        if (!focusPage) {
            return;
        }
        pageRefs.current.get(focusPage)?.scrollIntoView({ block: 'center' });
    }, [focusPage, highlightedElementKey]);
    return (_jsx("div", { className: className, "data-testid": 'consent-compiled-template-preview', children: _jsx(Document, { file: pdfDataUrl, onLoadSuccess: ({ numPages: n }) => setNumPages(n), options: pdfOptions, loading: _jsx("p", { className: 'text-sm text-neutral-600', children: "Loading PDF\u2026" }), children: _jsx("div", { className: 'flex flex-col items-center gap-4', children: Array.from({ length: numPages }, (_, i) => {
                    const pageNumber = i + 1;
                    const fields = fieldsByPage.get(pageNumber) ?? [];
                    const pageOverlays = overlaysByPage.get(pageNumber) ?? [];
                    return (_jsxs("div", { ref: (node) => {
                            if (node) {
                                pageRefs.current.set(pageNumber, node);
                            }
                            else {
                                pageRefs.current.delete(pageNumber);
                            }
                        }, className: 'relative inline-block', "data-testid": `consent-preview-page-wrap-${pageNumber}`, children: [_jsx(Page, { pageNumber: pageNumber, scale: PREVIEW_PAGE_SCALE, loading: null }), signatureFields
                                ? fields.map((field) => {
                                    const highlighted = highlightedElementKey === field.ec__key;
                                    const selectProps = onSelectElement
                                        ? {
                                            onClick: () => onSelectElement(field.ec__key),
                                            onKeyDown: (event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    onSelectElement(field.ec__key);
                                                }
                                            },
                                            role: 'button',
                                            tabIndex: 0,
                                        }
                                        : {};
                                    if (field.choiceMarker === 'checkbox' || field.choiceMarker === 'radio') {
                                        const color = field.roleColor ?? '#4F46E5';
                                        return (_jsx("div", { "data-testid": `configuration-choice-option-${field.title}`, style: {
                                                position: 'absolute',
                                                left: `${field.xPct}%`,
                                                top: `${field.yPct}%`,
                                                width: `${field.widthPct}%`,
                                                height: `${field.heightPct}%`,
                                            }, ...selectProps, children: _jsx("div", { className: 'flex h-full w-full items-center justify-center overflow-hidden px-0.5 text-[10px] font-semibold leading-none', style: {
                                                    border: `${highlighted ? 2 : 1.5}px solid ${color}`,
                                                    borderRadius: field.choiceMarker === 'radio' ? '9999px' : '2px',
                                                    backgroundColor: highlighted ? color : `${color}26`,
                                                    color: highlighted ? '#FFFFFF' : color,
                                                }, children: field.title }) }, `${pageNumber}-${field.ec__key}-${field.choiceMarker}-${field.xPct}-${field.yPct}`));
                                    }
                                    if (field.choiceMarker === 'group') {
                                        const color = field.roleColor ?? '#4F46E5';
                                        return (_jsx("div", { "data-testid": `configuration-choice-group-${field.title}`, className: 'flex items-center gap-1 whitespace-nowrap rounded px-1.5 text-[11px] font-semibold leading-none', style: {
                                                position: 'absolute',
                                                left: `${field.xPct}%`,
                                                top: `calc(${field.yPct}% - 26px)`,
                                                height: 22,
                                                backgroundColor: highlighted ? color : `${color}26`,
                                                border: `1px solid ${color}`,
                                                color: highlighted ? '#FFFFFF' : color,
                                                outline: highlighted ? `2px solid ${color}` : 'none',
                                                outlineOffset: 1,
                                            }, ...selectProps, children: field.title }, `${pageNumber}-${field.ec__key}-group-${field.xPct}-${field.yPct}`));
                                    }
                                    return (_jsx("div", { "data-testid": highlighted ? 'consent-preview-overlay-highlighted' : undefined, className: overlayClassName(overlayVariant, highlighted, Boolean(onSelectElement)), style: {
                                            left: `${field.xPct}%`,
                                            top: `${field.yPct}%`,
                                            width: `${field.widthPct}%`,
                                            height: `${field.heightPct}%`,
                                            ...(overlayVariant === 'configuration' && field.roleColor && !field.choiceMarker
                                                ? {
                                                    borderColor: field.roleColor,
                                                    backgroundColor: highlighted ? field.roleColor : `${field.roleColor}26`,
                                                }
                                                : {}),
                                        }, title: `${field.type} — ${field.role}`, ...selectProps, children: _jsx("span", { className: overlayVariant === 'configuration'
                                                ? `px-1 text-[10.5px] font-medium leading-[13px] ${highlighted ? 'text-white' : 'text-neutral-900'}`
                                                : 'px-1 text-[10px] font-semibold text-white drop-shadow', children: overlayVariant === 'configuration' ? field.title : `${field.type} (${field.role})` }) }, `${pageNumber}-${field.ec__key}-${field.xPct}-${field.yPct}`));
                                })
                                : pageOverlays.map((overlay) => (_jsx("div", { "data-testid": 'consent-preview-overlay', "data-overlay-id": overlay.id, className: 'pointer-events-none absolute z-10 box-border flex items-center justify-center border-2 border-blue-600 bg-blue-600/25 text-center', style: {
                                        left: `${overlay.xPct}%`,
                                        top: `${overlay.yPct}%`,
                                        width: `${overlay.widthPct}%`,
                                        height: `${overlay.heightPct}%`,
                                    }, title: previewCaption(overlay, translate), children: _jsx("span", { className: 'px-1 text-[10px] font-semibold text-white drop-shadow', children: previewCaption(overlay, translate) }) }, overlay.id)))] }, pageNumber));
                }) }) }) }));
};
